import { z } from 'zod';

/**
 * Schema de autenticação - Login request
 * AIDEV-NOTE: Email deve ser validado contra sync_vendedores no Firebird
 */
export const loginSchema = z.object({
  email: z.string().email('E-mail inválido'),
  senha: z.string().min(6, 'Senha deve ter no mínimo 6 caracteres'),
  lembrar: z.boolean().optional().default(false)
});

export type LoginInput = z.infer<typeof loginSchema>;

/**
 * Schema de refresh token
 */
export const refreshTokenSchema = z.object({
  refreshToken: z.string().min(1, 'Refresh token é obrigatório')
});

export type RefreshTokenInput = z.infer<typeof refreshTokenSchema>;

/**
 * Schema de resposta de autenticação bem-sucedida
 */
export const authResponseSchema = z.object({
  usuario: z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    nome: z.string(),
    vendedor_id: z.number().int().positive().nullable(),
    tabela_preco_padrao: z.number().int().positive().nullable(),
    pode_selecionar_tabela: z.boolean(),
    apenas_proprio: z.boolean(),
    gerente_id: z.number().int().positive().nullable(),
    perfil: z.enum(['admin', 'gerente', 'vendedor', 'representante']),
    client_id: z.number().int().positive()
  }),
  expiresIn: z.number().int().positive(),
  tokenType: z.literal('Bearer')
});

export type AuthResponse = z.infer<typeof authResponseSchema>;

/**
 * Schema de erro de autenticação
 */
export const authErrorSchema = z.object({
  error: z.string(),
  message: z.string(),
  code: z.enum([
    'INVALID_CREDENTIALS',
    'USER_NOT_FOUND',
    'VENDOR_INACTIVE',
    'TOKEN_EXPIRED',
    'INVALID_TOKEN',
    'REFRESH_TOKEN_EXPIRED',
    'GRACE_PERIOD_EXPIRED',
    'OFFLINE_AUTH_FAILED'
  ])
});

export type AuthError = z.infer<typeof authErrorSchema>;

/**
 * Schema para validação de vendedor no Firebird
 * AIDEV-NOTE: Espelho da estrutura VENDEDORES do Firebird
 */
export const vendedorFirebirdSchema = z.object({
  CODIGO: z.number().int().positive(),
  NOME: z.string(),
  EMAIL: z.string().email(),
  ATIVO: z.enum(['S', 'N']),
  TABELA_PRECO: z.number().int().positive().nullable(),
  CODIGO_PREPOSTO: z.number().int().positive().nullable()
});

export type VendedorFirebird = z.infer<typeof vendedorFirebirdSchema>;

/**
 * Claims do JWT - estrutura que vai no token
 */
export const jwtClaimsSchema = z.object({
  sub: z.string().uuid(),
  email: z.string().email(),
  vendedor_id: z.number().int().positive().nullable(),
  nome: z.string(),
  tabela_preco_padrao: z.number().int().positive().nullable(),
  pode_selecionar_tabela: z.boolean(),
  apenas_proprio: z.boolean(),
  gerente_id: z.number().int().positive().nullable(),
  perfil: z.enum(['admin', 'gerente', 'vendedor', 'representante']),
  client_id: z.number().int().positive(),
  // AIDEV-NOTE: Campos de políticas comerciais — calculados no login via getEfetivePolicies()
  // Marcados como optional para que tokens de refresh (payload mínimo) passem na validação.
  pode_dar_desconto_item: z.boolean().optional().default(false),
  limite_desconto_item: z.number().optional().default(0),
  pode_dar_desconto_total: z.boolean().optional().default(false),
  limite_desconto_total: z.number().optional().default(0),
  pode_cadastrar_clientes: z.boolean().optional().default(false),
  pode_alterar_origem_pedido: z.boolean().optional().default(true),
  pode_alterar_empresa: z.boolean().optional().default(true),
  config_sync_pedido: z.enum(['automatico', 'manual']).optional().default('manual'),
  origem_pedido_padrao_id: z.number().nullable().optional(),
  // AIDEV-NOTE: Ponto de estoque efetivo — filtra o saldo exibido. null = todos os pontos.
  ponto_estoque_id: z.number().nullable().optional(),
  empresa_padrao_id: z.number().nullable().optional(),
  // AIDEV-NOTE: IDs dos padrões configurados nas políticas — usados como fallback quando vendedor não seleciona
  tipo_comercializacao_padrao_id: z.number().nullable().optional(),
  tipo_movimento_receber_padrao_id: z.number().nullable().optional(),
  pode_alterar_tipo_movimento_receber: z.boolean().optional().default(true),
  exibir_estoque_vendedor: z.boolean().optional().default(true),
  // AIDEV-NOTE: tipo_preco_base e tem_regras_quantidade — controlam nível 5 do PricingService.
  // tipo_preco_base: config global de varejo ('V') ou atacado ('A').
  // tem_regras_quantidade: true se há regras_tabela_preco_quantidade configuradas para o client_id.
  tipo_preco_base: z.enum(['V', 'A']).optional().default('V'),
  tem_regras_quantidade: z.boolean().optional().default(false),
  // AIDEV-NOTE: Política de alteração de preço unitário — gerada em getEfetivePolicies().
  // Optional para compatibilidade com tokens emitidos antes desta feature.
  pode_alterar_preco_unitario: z.boolean().optional().default(false),
  limite_reducao_preco_pct: z.number().nonnegative().nullable().optional().default(null),
  limite_aumento_preco_pct: z.number().nonnegative().nullable().optional().default(null),
  // AIDEV-NOTE: regras_tabela_quantidade e restricoes_condicao_pagto_jwt NÃO estão no JWT
  // (overhead de tamanho). São populados apenas via GET /auth/me. Marcados como optional
  // para que o JWT payload passe na validação sem esses campos.
  regras_tabela_quantidade: z
    .array(
      z.object({
        tabela_preco_id: z.number(),
        quantidade_minima: z.number(),
        quantidade_maxima: z.number().nullable(),
        ordem: z.number()
      })
    )
    .optional()
    .default([]),
  restricoes_condicao_pagto_jwt: z
    .array(
      z.object({
        condicao_pagto_id: z.number(),
        ativo: z.boolean(),
        valor_minimo_pedido: z.number().nullable()
      })
    )
    .optional()
    .default([]),
  iat: z.number().int(),
  exp: z.number().int()
});

export type JWTClaims = z.infer<typeof jwtClaimsSchema>;

/**
 * Schema para usuário do sistema (tabela usuarios_sistema)
 * AIDEV-NOTE: Colunas reais no banco: cliente_id (não client_id), vendedor_erp_id (não vendedor_id)
 */
export const usuarioSistemaSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  vendedor_id: z.number().int().positive().nullable(),
  perfil: z.enum(['admin', 'gerente', 'vendedor', 'representante']),
  client_id: z.number().int().positive(),
  ativo: z.boolean().optional().default(true),
  config_sync_pedido: z.enum(['automatico', 'manual']).default('manual'),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime()
});

export type UsuarioSistema = z.infer<typeof usuarioSistemaSchema>;
